import { Component, OnInit } from '@angular/core';
import { AuthTestingService, TestResult, TokenInfo } from '../../services/auth-testing.service';
import { AuthService } from '../../services/auth.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-auth-test',
  templateUrl: './auth-test.component.html',
  styleUrls: ['./auth-test.component.css']
})
export class AuthTestComponent implements OnInit {
  
  // Test results
  testResults: TestResult[] = [];
  debugInfo: any = null;
  currentTokenInfo: TokenInfo | null = null;
  
  // UI state
  isLoading = false;
  selectedScope = 'access_as_user';
  customEndpoint = 'Ticket/GetTicketPage';
  
  // Available scopes
  availableScopes = {
    'access_as_user': environment.azureAuthenticationConfig.scopes.userAccess,
    'helpdesk': environment.azureAuthenticationConfig.scopes.helpdesk,
    'graph': environment.azureAuthenticationConfig.scopes.graph,
    'all': environment.azureAuthenticationConfig.scopes.all
  };

  constructor(
    private authTestService: AuthTestingService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadDebugInfo();
  }

  /**
   * Load authentication debug information
   */
  loadDebugInfo(): void {
    this.authTestService.getAuthDebugInfo().subscribe(info => {
      this.debugInfo = info;
    });
  }

  /**
   * Test user delegation flow with selected scope
   */
  testDelegationFlow(): void {
    this.isLoading = true;
    const scopes = this.selectedScope === 'all' 
      ? this.availableScopes.all 
      : [this.availableScopes[this.selectedScope as keyof typeof this.availableScopes] as string];

    this.authTestService.testUserDelegationFlow(scopes).subscribe(result => {
      this.testResults.unshift({
        ...result,
        message: `Delegation Flow Test (${this.selectedScope}): ${result.message}`
      });
      
      if (result.tokenInfo) {
        this.currentTokenInfo = result.tokenInfo;
      }
      
      this.isLoading = false;
    });
  }

  /**
   * Test the problematic GetTicketPage endpoint
   */
  testGetTicketPage(): void {
    this.isLoading = true;
    this.authTestService.testGetTicketPage().subscribe(result => {
      this.testResults.unshift({
        ...result,
        message: `GetTicketPage Test: ${result.message}`
      });
      this.isLoading = false;
    });
  }

  /**
   * Test custom API endpoint
   */
  testCustomEndpoint(): void {
    if (!this.customEndpoint.trim()) {
      return;
    }

    this.isLoading = true;
    const method = this.customEndpoint.toLowerCase().includes('get') ? 'GET' : 'POST';
    
    this.authTestService.testApiCall(this.customEndpoint, method).subscribe(result => {
      this.testResults.unshift({
        ...result,
        message: `Custom Endpoint Test (${this.customEndpoint}): ${result.message}`
      });
      this.isLoading = false;
    });
  }

  /**
   * Generate cURL command for manual testing
   */
  generateCurl(): void {
    this.authTestService.generateCurlCommand('Ticket/GetTicketPage', 'POST', {
      page: 0,
      pageSize: 10,
      keyword: "",
      ticketStatusIDs: [],
      categoryIDs: [],
      assignedToTeamIDs: [],
      resolutionTypeID: 0,
      isDescTicketNo: true,
      isDescAcceptedBy: true,
      isDescSubmittedBy: true,
      isDescClosedBy: true,
      isDescSubmittedDate: true,
      isDescAcceptedDate: true,
      isDescClosedDate: true
    }).subscribe(curlCommand => {
      navigator.clipboard.writeText(curlCommand).then(() => {
        this.testResults.unshift({
          success: true,
          message: 'cURL command copied to clipboard!',
          data: curlCommand
        });
      });
    });
  }

  /**
   * Request helpdesk access specifically
   */
  requestHelpdeskAccess(): void {
    this.isLoading = true;
    this.authService.requestHelpdeskAccess().subscribe(success => {
      this.testResults.unshift({
        success: success,
        message: success 
          ? 'Helpdesk access granted successfully!' 
          : 'Failed to obtain helpdesk access'
      });
      this.isLoading = false;
      
      if (success) {
        this.loadDebugInfo();
      }
    });
  }

  /**
   * Get authentication status
   */
  checkAuthStatus(): void {
    this.authService.getAuthStatus().subscribe(status => {
      this.testResults.unshift({
        success: status.isAuthenticated,
        message: `Auth Status: ${status.isAuthenticated ? 'Authenticated' : 'Not authenticated'}`,
        data: status
      });
    });
  }

  /**
   * Clear test results
   */
  clearResults(): void {
    this.testResults = [];
    this.currentTokenInfo = null;
  }

  /**
   * Format JSON for display
   */
  formatJson(obj: any): string {
    return JSON.stringify(obj, null, 2);
  }

  /**
   * Get CSS class for test result
   */
  getResultClass(result: TestResult): string {
    if (result.success) {
      return 'result-success';
    } else if (result.statusCode === 403) {
      return 'result-forbidden';
    } else {
      return 'result-error';
    }
  }

  /**
   * Copy text to clipboard
   */
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
      console.log('Copied to clipboard');
    });
  }
}